import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Row, Col } from 'antd';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormLink from 'components-desktop/FormLink';
import FormInput from 'components-desktop/FormInput';
import FormInputNumber from 'components-desktop/FormInputNumber';
import helpers from 'misc/helpers';

const propTypes = {
  invoice: PropTypes.object.isRequired,
  payment: PropTypes.object.isRequired,
  clearInvoice: PropTypes.func.isRequired,
  onInvoicePaymentApply: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
};

@autobind
class FormApplyPayment extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      isApplyButtonEnable: true,
    };
  }

  async applyPayment(values) {
    const { invoice, payment, clearInvoice, onInvoicePaymentApply } = this.props;

    // The applying invoice should be of the same company as the payment.
    if (invoice.getLong('companyId') !== payment.getLong('companyId')) {
      this.notifyError('Error', `This invoice does not belong to ${payment.getString('companyName')}.`);
      return;
    }

    try {
      this.setState({
        isApplyButtonEnable: false,
      });

      const params = {
        invoiceId: invoice.getId(),
        paymentId: payment.getId(),
        received: helpers.extraFormInputNumberValue(values, 'received'),
        remark: helpers.extraFormInputValue(values, 'remark'),
      };
      await this.callService('/invoice_payment/add', params);

      // Reload invoice payments table.
      onInvoicePaymentApply();

      // Hide the apply form.
      setTimeout(() => {
        clearInvoice();
      }, 500);

      this.notifySuccess('Success', 'Successfully applied payment to invoice.');
    } finally {
      this.setState({
        isApplyButtonEnable: true,
      });
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.applyPayment(values);
      }
    });
  }

  onCancelClick() {
    this.props.clearInvoice();
  }

  renderContent() {
    const { invoice, form } = this.props;
    const { isApplyButtonEnable } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <styledComp.Div
          width="match_parent"
          height="1px"
          margin="16px 16px"
          backgroundColor="#000000"
        />

        <styledComp.AntdForm
          width="match_parent"
          height="wrap_content"
          onSubmit={this.handleSubmit}
        >
          {/* Content */}
          <FlexColContainer
            width="match_parent"
            height="wrap_content"
            padding="20px 20px"
          >
            <Row type="flex" gutter={24}>
              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormLink
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  label="Invoice Number"
                  href={`/invoices/invoice_details?id=${invoice.getId()}`}
                  text={invoice.getString('invoiceNumber')}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormLink
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  label="Patient"
                  href={`/patients/patient_profile?id=${invoice.getLong('patientId')}`}
                  text={invoice.getString('patientName')}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="invoiceDate"
                  label="Invoice Date"
                  disabled
                  initialValue={invoice.getString('invoiceDateUsString')}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="mailDate"
                  label="Mail Date"
                  disabled
                  initialValue={invoice.getString('mailDateUsString')}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="authorizationCode"
                  label="Authorization Code"
                  disabled
                  initialValue={invoice.getString('authorizationCode')}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="amount"
                  label="Invoice Amount Billed"
                  disabled
                  initialValue={helpers.toDollars(invoice.getDouble('amount'))}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="totalReceived"
                  label="Invoice Amount Received"
                  disabled
                  initialValue={helpers.toDollars(invoice.getDouble('totalReceived'))}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="balanceDue"
                  label="Invoice Balance Due"
                  disabled
                  initialValue={helpers.toDollars(invoice.getDouble('balanceDue'))}
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                <FormInputNumber
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="received"
                  label="Amount Received"
                  placeholder="0.00"
                  min={0.01}
                  step={0.01}
                  precision={2}
                  extra="Amount to be applied to the invoice"
                  isRequired
                />
              </Col>

              <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 16 }} lg={{ span: 18 }}>
                <FormInput
                  width="match_parent"
                  height="wrap_content"
                  form={form}
                  name="remark"
                  label="Remark"
                  max={2000}
                  extra="For internal use only."
                />
              </Col>
            </Row>
          </FlexColContainer>

          {/* Footer */}
          <FlexRowContainer
            width="match_parent"
            height="wrap_content"
            padding="20px 20px"
            justifyContent="center"
            backgroundColor="containerGrayBackground"
          >
            <CustomButton
              width="wrap_content"
              height="32px"
              margin="0 4px 0 0"
              backgroundColor="yellow_gold"
              fontSize="14px"
              type="primary"
              loading={!isApplyButtonEnable}
              htmlType="submit"
            >
              APPLY PAYMENT
            </CustomButton>

            <styledComp.AntdButton
              width="wrap_content"
              height="32px"
              fontSize="14px"
              type="default"
              onClick={this.onCancelClick}
            >
              CANCEL
            </styledComp.AntdButton>
          </FlexRowContainer>
        </styledComp.AntdForm>
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
FormApplyPayment.propTypes = propTypes;

export default Form.create()(FormApplyPayment);
